import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { AssetContainerModel } from 'js/src/client';
import type { ImmutableMap } from 'js/src/libs/consts';

// Define a type for the slice state
export interface ArticleState {
    article_id: number | null;
    article_url: string;
    article_media: Record<string, ImmutableMap<AssetContainerModel>>;
    article_language_code: string;
}

// Define the initial state using that type
const initialState: ArticleState = {
    article_id: null,
    article_url: '',
    article_media: {},
    article_language_code: '',
};

interface PayloadMeta {
    systemTriggered: boolean;
    parentActionGroup?: string;
}

export const articleSlice = createSlice({
    name: 'article',
    // `createSlice` will infer the state type from the `initialState` argument
    initialState,
    reducers: {
        storeArticleId: (
            state,
            action: PayloadAction<
                {
                    articleId: number | null;
                } & PayloadMeta
            >,
        ) => {
            state.article_id = action.payload.articleId;
        },
        storeArticleUrl: (
            state,
            action: PayloadAction<
                {
                    articleUrl: string;
                } & PayloadMeta
            >,
        ) => {
            state.article_url = action.payload.articleUrl;
        },
        storeArticleMedia: (
            state,
            action: PayloadAction<
                {
                    articleMedia: typeof initialState.article_media;
                } & PayloadMeta
            >,
        ) => {
            return {
                ...state,
                article_media: action.payload.articleMedia,
            };
        },
        storeArticleLanguageCode(
            state,
            action: PayloadAction<
                {
                    articleLanguageCode: string;
                } & PayloadMeta
            >,
        ) {
            state.article_language_code = action.payload.articleLanguageCode;
        },
    },
});

export const { storeArticleId, storeArticleUrl, storeArticleMedia, storeArticleLanguageCode } =
    articleSlice.actions;
export const articleActionTypes = Object.values(articleSlice.actions).map((action) => action.type);

export const articleReducer = articleSlice.reducer;
